import {
  AccountBalance as BudgetIcon,
  Dashboard as DashboardIcon,
  Logout as LogoutIcon,
  Menu as MenuIcon,
  Settings as SettingsIcon,
  Receipt as TransactionIcon,
  Help as HelpIcon,
  Analytics as AnalyticsIcon
} from '@mui/icons-material';
import {
  AppBar,
  Box,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Tooltip,
  useTheme,
  useMediaQuery
} from '@mui/material';
import React from 'react';
import { Outlet, Link as RouterLink, useLocation } from 'react-router-dom';
import Logo from './common/Logo';
import LogoText from './common/LogoText';
import { useSession } from '../contexts/SessionContext';

const drawerWidth = 240;
const collapsedDrawerWidth = 64;

const Layout: React.FC = () => {
  const theme = useTheme();
  const location = useLocation();
  const { logout } = useSession();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [mobileOpen, setMobileOpen] = React.useState(false);
  
  // Initialize drawer state from localStorage - only for desktop
  const [isDrawerCollapsed, setIsDrawerCollapsed] = React.useState(() => {
    if (typeof window !== 'undefined' && window.innerWidth >= theme.breakpoints.values.sm) {
      const saved = localStorage.getItem('drawerCollapsed');
      return saved ? JSON.parse(saved) : false;
    }
    return false;
  });

  // Save drawer state to localStorage whenever it changes (desktop only)
  React.useEffect(() => {
    if (!isMobile) {
      localStorage.setItem('drawerCollapsed', JSON.stringify(isDrawerCollapsed));
    }
  }, [isDrawerCollapsed, isMobile]);

  const handleDrawerToggle = () => {
    if (isMobile) {
      // On mobile, toggle drawer open/close
      setMobileOpen(!mobileOpen);
    } else {
      // On desktop, toggle drawer collapse
      setIsDrawerCollapsed(!isDrawerCollapsed);
    }
  };

  const handleLogout = () => {
    // Close the mobile drawer if open
    setMobileOpen(false);
    
    // Use the SessionContext logout function for proper cleanup
    logout();
  };

  const menuItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' },
    { text: 'Budgets', icon: <BudgetIcon />, path: '/budgets' },
    { text: 'Transactions', icon: <TransactionIcon />, path: '/transactions' },
    { text: 'Analytics', icon: <AnalyticsIcon />, path: '/analytics' },
    { text: 'Support', icon: <HelpIcon />, path: '/support' }
  ];

  // On mobile, never collapse the drawer content - always show icons and text
  const shouldShowText = isMobile || !isDrawerCollapsed;
  const currentDrawerWidth = isMobile ? drawerWidth : (isDrawerCollapsed ? collapsedDrawerWidth : drawerWidth);

  const drawer = (
    <Box sx={{ 
      height: '100%', 
      display: 'flex', 
      flexDirection: 'column',
      overflow: 'hidden'
    }}>
      <Toolbar 
        sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          px: shouldShowText ? 3 : 2,
          py: 2
        }}
      >
        <Box className="app-logo">
          <Logo variant="small" forceDark={true} />
        </Box>
      </Toolbar>
      <List>
        {menuItems.map((item) => (
          <Tooltip 
            key={item.text}
            title={(!shouldShowText && !isMobile) ? item.text : ""}
            placement="right"
            disableHoverListener={shouldShowText || isMobile}
          >
            <ListItem
              button
              component={RouterLink}
              to={item.path}
              selected={location.pathname === item.path}
              onClick={() => isMobile && setMobileOpen(false)}
              sx={{
                justifyContent: shouldShowText ? 'flex-start' : 'center',
                px: shouldShowText ? 3 : 2
              }}
            >
              <ListItemIcon sx={{ 
                minWidth: shouldShowText ? 40 : 'auto',
                mr: shouldShowText ? 2 : 0,
                justifyContent: 'center'
              }}>
                {item.icon}
              </ListItemIcon>
              {shouldShowText && <ListItemText primary={item.text} />}
            </ListItem>
          </Tooltip>
        ))}
      </List>
      <Divider />
      <List>
        <Tooltip 
          title={(!shouldShowText && !isMobile) ? "Settings" : ""}
          placement="right"
          disableHoverListener={shouldShowText || isMobile}
        >
          <ListItem
            button
            component={RouterLink}
            to="/settings"
            selected={location.pathname === '/settings'}
            onClick={() => isMobile && setMobileOpen(false)}
            sx={{
              justifyContent: shouldShowText ? 'flex-start' : 'center',
              px: shouldShowText ? 3 : 2
            }}
          >
            <ListItemIcon sx={{ 
              minWidth: shouldShowText ? 40 : 'auto',
              mr: shouldShowText ? 2 : 0,
              justifyContent: 'center'
            }}>
              <SettingsIcon />
            </ListItemIcon>
            {shouldShowText && <ListItemText primary="Settings" />}
          </ListItem>
        </Tooltip>
        <Tooltip 
          title={(!shouldShowText && !isMobile) ? "Logout" : ""}
          placement="right"
          disableHoverListener={shouldShowText || isMobile}
        >
          <ListItem 
            button 
            onClick={handleLogout}
            sx={{
              justifyContent: shouldShowText ? 'flex-start' : 'center',
              px: shouldShowText ? 3 : 2
            }}
          >
            <ListItemIcon sx={{ 
              minWidth: shouldShowText ? 40 : 'auto',
              mr: shouldShowText ? 2 : 0,
              justifyContent: 'center'
            }}>
              <LogoutIcon />
            </ListItemIcon>
            {shouldShowText && <ListItemText primary="Logout" />}
          </ListItem>
        </Tooltip>
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={{
          width: { 
            sm: `calc(100% - ${currentDrawerWidth}px)` 
          },
          ml: { 
            sm: `${currentDrawerWidth}px` 
          },
          transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2 }}
          >
            <MenuIcon />
          </IconButton>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box className="app-logo">
              <Logo variant="regular" forceDark={true} />
            </Box>
            <Box sx={{ ml: 1 }} className="app-title">
              <LogoText />
            </Box>
          </Box>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ 
          width: { sm: currentDrawerWidth }, 
          flexShrink: { sm: 0 },
          transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
        }}
        aria-label="navigation"
      >
        {/* Mobile Drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
            },
          }}
        >
          {drawer}
        </Drawer>
        {/* Desktop Drawer */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: currentDrawerWidth,
              transition: theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
              }),
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 1, sm: 2, md: 3 },
          width: { 
            sm: `calc(100% - ${currentDrawerWidth}px)` 
          },
          transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
        }}
      >
        <Toolbar />
        <Outlet />
      </Box>
    </Box>
  );
};

export default Layout; 